// Die Dachplanung im Büro anhängen (seit E3b, O70 – Gernot, 22.10.2026:
// „unkompliziert … einfach einen Sinn ergeben und leicht mitzuerledigen“).
// Heute macht das Büro die Dachplanung, nicht der Verkäufer vor Ort (Manuel
// am Telefon, 22.10.) – also braucht das CRM „Dachplanung anhängen“: das
// K2-PDF wählen, es geht roh an den Datei-Eingang des CRM (nur PDF), danach
// hängt es an der Anlage (PATCH, „dachplanung“). Ohne „bitte ansehen“ – das
// Büro hat es selbst angehängt (server/src/crm/berichtigen.ts).
//
// Geprüft wird vorher hier, was der Server ohnehin prüft – damit eine falsche
// Datei gleich am Knopf eine klare Meldung bekommt, nicht erst nach dem
// Hochladen.

import { DATEI_ARTEN, type BueroAntwort } from "@happy-energy/kern";
import { aendern, dateiSenden } from "@happy-energy/oberflaeche";

/** Die größte Datei, die der Server als Dachplanung annimmt (das K2-PDF). */
export const PDF_GROESSTE = DATEI_ARTEN["application/pdf"];

/** Passt die gewählte Datei? null: ja; sonst, was nicht passt, in einem Satz. */
export function pdfPruefen(datei: { type: string; size: number; name: string }): string | null {
  const istPdf = datei.type === "application/pdf" || /\.pdf$/i.test(datei.name);
  if (!istPdf) return "Das geht nur mit einem PDF – bitte das K2 als PDF wählen.";
  if (datei.size === 0) return "Die Datei ist leer.";
  if (datei.size > PDF_GROESSTE) return `Das PDF ist zu groß – höchstens ${Math.round(PDF_GROESSTE / 1_000_000)} MB.`;
  return null;
}

/** Beginnt der Inhalt wie ein PDF („%PDF-“)? Ein umbenanntes Foto fällt so gleich auf. */
export function siehtAusWiePdf(anfang: Uint8Array): boolean {
  return anfang.length >= 5 && String.fromCharCode(...anfang.slice(0, 5)) === "%PDF-";
}

/** Die Prüfsumme (SHA-256, Kleinbuchstaben) – sie ist der Name der Datei am Server. */
export async function pruefsummeVon(daten: ArrayBuffer): Promise<string> {
  const hash = await crypto.subtle.digest("SHA-256", daten);
  return [...new Uint8Array(hash)].map((b) => b.toString(16).padStart(2, "0")).join("");
}

/**
 * Das K2-PDF an die Anlage hängen: prüfen, hochladen, anhängen. Wirft mit
 * einer Meldung für Menschen, wenn die Datei nicht passt; Fehler des Servers
 * (ApiFehler) gehen weiter, die Seite sagt sie.
 */
export async function dachplanungAnhaengen(anlageId: string, datei: File): Promise<void> {
  const fehler = pdfPruefen(datei);
  if (fehler) throw new Error(fehler);
  const daten = await datei.arrayBuffer();
  if (!siehtAusWiePdf(new Uint8Array(daten, 0, Math.min(5, daten.byteLength)))) throw new Error("Diese Datei ist kein PDF.");
  const pruefsumme = await pruefsummeVon(daten);
  await dateiSenden(`/api/crm/datei/${pruefsumme}`, new Blob([daten], { type: "application/pdf" }));
  await aendern<BueroAntwort>(`/api/crm/anlage/${anlageId}`, { dachplanung: { pruefsumme } });
}
